import { GridItem, GridRow, gridConfig } from '@tvg/fuse-design/responsive-grid';
import { PLPQueryData, usePLPQuery } from 'client/api/plp-results';
import { useGridUpdateEvent } from 'client/hooks/useEvents';
import useIsUpdating from 'client/hooks/useIsUpdating';
import { useProductSFL } from 'client/hooks/useProductSFL';
import useGlobalConfig from 'client/state/GlobalConfig';
import { PageType } from 'common/types/global';
import { GroupedFacet } from 'common/types/search-response';
import React, { Suspense, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { withPlacementContainers } from './Placements';
import { ProductCard } from './ProductCard';

const StyledGridRow = styled(GridRow)`
  &.productList {
    margin-top: var(--spacing-space16);
    margin-bottom: 0px;
    row-gap: 1.5rem;

    @media screen and (min-width: ${gridConfig.m.minWidth}) {
      margin-top: var(--spacing-space16);
      row-gap: 2rem;
    }

    @media screen and (min-width: ${gridConfig.l.minWidth}) {
      margin-top: var(--spacing-space32);
      row-gap: 3rem;
      column-gap: 1rem;
    }
  }
`;

const NoResultsAlert = React.lazy(() => import('../Alert/NoResultsAlert'));

export function ProductCardList() {
  const { brand, pageType } = useGlobalConfig();
  const { data } = usePLPQuery(
    useCallback(
      (d: PLPQueryData) => ({
        products: d.products,
        facets: d.facets,
        category: d.category,
        numResults: d.numResults,
        collection: d.collection,
      }),
      [],
    ),
  );

  const isUpdating = useIsUpdating();
  const memoizedProducts = useMemo(() => data?.products || [], [data?.products]);
  const savedProducts = useProductSFL(data?.products);
  const productList = useMemo(() => {
    if (!memoizedProducts.length || !data?.facets) return [];
    const brandsMap: Record<string, string> = {};

    const brandFacet = data.facets.find((facet) => facet.id === 'brand') as GroupedFacet | undefined;
    brandFacet?.values.forEach((b) => {
      b.items.forEach((brandItem) => {
        brandsMap[brandItem.id] = brandItem.name;
      });
    });

    const productListWithBrandNames = memoizedProducts.map((product) => {
      const brandId = product.brand;

      return {
        ...product,
        brand: brandsMap[brandId] || brandId,
      };
    });

    return productListWithBrandNames;
  }, [memoizedProducts, data?.facets]);

  useGridUpdateEvent();

  if (!data || !productList?.length) {
    return (
      <GridRow className="row">
        <GridItem xxs="12">
          <Suspense fallback={null}>
            <NoResultsAlert />
          </Suspense>
        </GridItem>
      </GridRow>
    );
  }

  return (
    <StyledGridRow
      className="row productList"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(pageType === PageType.search && { 'data-cnstrc-search': true })}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(pageType === PageType.browse && { 'data-cnstrc-browse': true })}
      data-cnstrc-filter-name={pageType === PageType.browse ? 'group_id' : undefined}
      data-cnstrc-filter-value={pageType === PageType.browse ? data.category.group_id : undefined}
      data-cnstrc-num-results={data.numResults}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(pageType === PageType.promo && {
        'data-cnstrc-browse': true,
        'data-cnstrc-filter-name': 'collection_id',
        'data-cnstrc-filter-value': data.collection?.id,
      })}
    >
      {withPlacementContainers(
        productList.map((item, itemIndex) => (
          <React.Fragment key={item.product_id}>
            <GridItem xxs="6" m="4" className="">
              <ProductCard
                product={item}
                productIndex={itemIndex}
                isSaved={savedProducts[item.product_id]}
                globalBrand={brand}
                isSkeleton={isUpdating}
              />
            </GridItem>
          </React.Fragment>
        )),
      )}
    </StyledGridRow>
  );
}
